import {
  isFilled,
  type RichTextField,
  type RTImageNode,
  type RTNode,
} from '@prismicio/client';

import type {
  RichContentBlockColumn,
  RichContentBlockColumnKind,
  RichContentBlockProps,
} from '_components/RichContentBlock/types';

/**
 * Rich text labels the block gives meaning to. Defined on the model in
 * `components/cms/SliceMachineSlices/RichContentBlock/model.json`.
 *
 * DON'T RENAME THESE once authors use them. Prismic saves the label's name
 * inside each document's content, so renaming a label in the model leaves
 * every existing quote on the old name, and those quotes then render as plain
 * paragraphs with no error. If a rename is unavoidable, match the old and new
 * names here until every document is re-saved. The model spec fails if these
 * and the model's labels drift apart. Authoring guide:
 * `docs/RICH_CONTENT_BLOCK_AUTHORING.md`.
 */
export const PULL_QUOTE_LABEL = 'Pull quote';
export const ATTRIBUTION_LABEL = 'Quote attribution';

/** Variation IDs on the model. Any other value reads as one column. */
export const RICH_CONTENT_BLOCK_VARIATION = {
  oneColumn: 'default',
  twoColumns: 'twoColumns',
} as const;

/**
 * The subset of a `rich_content_block` shared slice this module relies on,
 * and the base of `BrandedStoreSliceType.richContentBlock`'s slice type.
 * Field contract: `richContentBlock.schema.md`.
 */
export interface RichContentBlockSlice {
  id?: string;
  slice_type?: string;
  /** Usually a `RICH_CONTENT_BLOCK_VARIATION` value; anything else is one
      column. */
  variation?: string;
  primary?: {
    content?: RichTextField | null;
    left?: RichTextField | null;
    right?: RichTextField | null;
  } | null;
}

export const hasLabel = (node: RTNode, label: string): boolean =>
  'spans' in node &&
  node.spans.some((span) => span.type === 'label' && span.data.label === label);

const isHeading = (node: RTNode) => /^heading[2-6]$/.test(node.type);

// An empty paragraph is what Prismic leaves behind when an author presses
// return at the end of a field; it shouldn't change the column's kind.
const isBlank = (node: RTNode) =>
  node.type !== 'image' && !('text' in node && node.text.trim());

/**
 * The image composition's frame on desktop (Figma 596:11077). An image
 * smaller than this in either direction would have to be stretched to fill
 * it, so it renders at its natural size in a copy column instead.
 */
export const IMAGE_FRAME = { width: 678, height: 542 } as const;

const fillsImageFrame = (node: RTNode) =>
  node.type === 'image' &&
  node.dimensions.width >= IMAGE_FRAME.width &&
  node.dimensions.height >= IMAGE_FRAME.height;

const kindOf = (nodes: RTNode[]): RichContentBlockColumnKind => {
  // The composition shows the first image, so that's the one that must fill it.
  if (nodes.every((node) => node.type === 'image')) {
    return fillsImageFrame(nodes[0]) ? 'image' : 'text';
  }
  // Any text block the author labels Pull quote renders as quote text, so a
  // column that opens with one is a quote column whatever its block type.
  if (hasLabel(nodes[0], PULL_QUOTE_LABEL)) return 'quote';
  if (nodes.every(isHeading)) return 'heading';
  return 'text';
};

const toColumn = (
  field: RichTextField | null | undefined
): RichContentBlockColumn | null => {
  if (!isFilled.richText(field)) return null;

  const nodes = field.filter((node) => !isBlank(node));
  return nodes.length ? { kind: kindOf(nodes), content: field } : null;
};

/**
 * Maps a `rich_content_block` slice to `RichContentBlock` props. The
 * `twoColumns` variation reads `left` and `right`; anything else (the
 * `default`, one-column variation) reads `content`. A two-column block with
 * one empty column renders the other on its own. Returns `null` when there's
 * nothing to show, so the page never renders an empty block.
 */
export const transformRichContentBlock = (
  slice: RichContentBlockSlice | null
): RichContentBlockProps | null => {
  const fields = slice?.primary ?? {};
  const [first, second] = (
    slice?.variation === RICH_CONTENT_BLOCK_VARIATION.twoColumns
      ? [fields.left, fields.right]
      : [fields.content]
  )
    .map(toColumn)
    .filter((column): column is RichContentBlockColumn => column !== null);

  if (!first) return null;
  return { columns: second ? [first, second] : [first] };
};

const textOf = (node: RTNode) => ('text' in node ? node.text.trim() : '');

/**
 * The text of the first pull quote: its run of Pull quote blocks, joined
 * with spaces, without the attribution. `null` when there's no quote.
 */
const firstQuote = (nodes: RTNode[]): string | null => {
  const start = nodes.findIndex(
    (node) => hasLabel(node, PULL_QUOTE_LABEL) && !isBlank(node)
  );
  if (start === -1) return null;

  const end = nodes.findIndex(
    (node, i) => i > start && !hasLabel(node, PULL_QUOTE_LABEL)
  );
  return nodes
    .slice(start, end === -1 ? undefined : end)
    .map(textOf)
    .filter(Boolean)
    .join(' ');
};

/**
 * Where a sentence ends: a `.`, `!` or `?` followed by a space or the end
 * of the text. A period doesn't count when it has another period one or two
 * characters before it, or follows a lone capital letter, so abbreviations
 * ("e.g.", "U.S."), ellipses ("...") and initials ("J. Smith") stay inside
 * the sentence.
 */
const sentenceEnd = (text: string): number | undefined =>
  [...text.matchAll(/[.!?](?=\s|$)/g)]
    .map(({ index }) => index)
    .find(
      (i) =>
        text[i] !== '.' ||
        (text[i - 1] !== '.' &&
          text[i - 2] !== '.' &&
          !/(^|\s)[A-Z]$/.test(text.slice(0, i)))
    );

/**
 * The first sentence of the first body copy, or all of it when it has no
 * sentence ending. `null` when there's no body copy.
 */
const firstSentence = (nodes: RTNode[]): string | null => {
  // `isBlank` keeps images, which have no text to send.
  const body = nodes.find(
    (node) =>
      node.type !== 'image' &&
      !isBlank(node) &&
      !hasLabel(node, PULL_QUOTE_LABEL) &&
      !hasLabel(node, ATTRIBUTION_LABEL)
  );
  if (!body) return null;

  const text = textOf(body);
  const end = sentenceEnd(text);
  return end === undefined ? text : text.slice(0, end + 1);
};

/**
 * The first image's file name, e.g. `aUwyZ3NYClf9oopk_hike-hero-FR.png`:
 * Prismic's asset ID, then the name it was uploaded with. The full URL
 * often runs past GA4's 100-character limit, cutting off the part that
 * tells similar images apart. `null` with no image.
 */
const firstImage = (nodes: RTNode[]): string | null => {
  const image = nodes.find(
    (node): node is RTImageNode => node.type === 'image' && !!node.url
  );
  if (!image) return null;

  const name = image.url.split('?')[0].split('/').pop() ?? '';
  try {
    return decodeURIComponent(name);
  } catch {
    return name;
  }
};

const withType = (type: string, value: string | null) =>
  value ? `${type}: ${value}` : null;

/**
 * What identifies the block in analytics (ED-592), prefixed with where it
 * came from so Digi Merch can tell which block an event is for. The first
 * of, across the columns in reading order:
 *
 * - `heading: …` the first non-blank heading. One labelled Pull quote or
 *   Quote attribution renders as quote text, so it's skipped.
 * - `quote: …` the first pull quote
 * - `body: …` the first sentence of body copy
 * - `img: …` the first image's file name
 *
 * `null` only when the block has none of them.
 */
export const richContentBlockHeading = (
  columns: RichContentBlockColumn[]
): string | null => {
  const nodes = columns.flatMap((column) => column.content as RTNode[]);
  const heading = nodes.find(
    (node) =>
      isHeading(node) &&
      !isBlank(node) &&
      !hasLabel(node, PULL_QUOTE_LABEL) &&
      !hasLabel(node, ATTRIBUTION_LABEL)
  );

  return (
    withType('heading', heading ? textOf(heading) : null) ??
    withType('quote', firstQuote(nodes)) ??
    withType('body', firstSentence(nodes)) ??
    withType('img', firstImage(nodes))
  );
};

export default transformRichContentBlock;
